import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ConfiguratorFileConnectionService, DatastoreDto, FileConnection } from '@tj/tj-dmp-lib-api';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
    selector: 'dmp-file-connections',
    templateUrl: './file-connections.component.html',
    styleUrls: ['./file-connections.component.scss'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
/**
 * Connexions S3 / FTP d'un périmètre : celles d'un affilié (`partner` renseigné) ou celles du groupe (`partner` vide).
 * Affiché en popup depuis la fiche d'un groupe d'affiliés.
 */
export class FileConnectionsComponent implements OnChanges {

    @Input() datastore: DatastoreDto;
    /** Code de l'affilié ; vide pour les connexions du groupe. */
    @Input() partner: string | null = null;

    typeOptions: { label: string; value: string }[];

    connections: FileConnection[] = [];

    loading = false;
    saving = false;
    testing = false;

    formVisible = false;
    editing: FileConnection | null = null;
    form: FormGroup;

    constructor(
        private fb: FormBuilder,
        private fileConnectionService: ConfiguratorFileConnectionService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private translate: TranslateService
    ) {
        this.typeOptions = [
            { label: 'S3', value: 'S3' },
            { label: this.t('fileConnections.typeFtp'), value: 'FTP' }
        ];
        this.form = this.fb.group({
            name: ['', [Validators.required, this.uniqueNameValidator]],
            description: [''],
            type: ['S3', Validators.required],
            s3: this.fb.group({
                endpoint: ['', Validators.required],
                region: ['', Validators.required],
                bucketName: ['', Validators.required],
                accessKey: ['', Validators.required],
                secretKey: ['']
            }),
            ftp: this.fb.group({
                host: ['', Validators.required],
                login: ['', Validators.required],
                password: [''],
                ftps: [false],
                implicite: [false],
                passive: [true]
            })
        });
        this.form.get('type').valueChanges.subscribe(type => this.applyType(type));
        this.applyType('S3');
    }

    /**
     * Un nom est unique dans un périmètre (un affilié, ou le groupe) : la liste affichée est justement celle de
     * ce périmètre. Le serveur refuse aussi un doublon (cas d'un ajout simultané par quelqu'un d'autre).
     */
    private readonly uniqueNameValidator = (control: AbstractControl): ValidationErrors | null => {
        const name = (control.value ?? '').trim().toLowerCase();
        if (!name) {
            return null;
        }
        const taken = this.connections.some(c => c.id !== this.editing?.id && (c.name ?? '').trim().toLowerCase() === name);
        return taken ? { nameTaken: true } : null;
    };

    get isS3(): boolean {
        return this.form.getRawValue().type === 'S3';
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.datastore || changes.partner) {
            this.connections = [];
            if (this.datastore) {
                this.load();
            }
        }
    }

    openDialog(connection?: FileConnection) {
        this.editing = connection ?? null;
        const s3 = connection?.s3;
        const ftp = connection?.ftp;
        this.form.reset({
            name: connection?.name ?? '',
            description: connection?.description ?? '',
            type: connection?.type ?? 'S3',
            // Les secrets ne sont jamais affichés : vides, ils conservent la valeur enregistrée.
            s3: {
                endpoint: s3?.endpoint ?? '',
                region: s3?.region ?? '',
                bucketName: s3?.bucketName ?? '',
                accessKey: s3?.accessKey ?? '',
                secretKey: ''
            },
            ftp: {
                host: ftp?.host ?? '',
                login: ftp?.login ?? '',
                password: '',
                ftps: ftp?.ftps ?? false,
                implicite: ftp?.implicite ?? false,
                passive: ftp?.passive ?? true
            }
        });
        // Le type d'une connexion enregistrée ne change plus.
        if (connection) {
            this.form.get('type').disable();
        } else {
            this.form.get('type').enable();
        }
        this.applyType(this.form.getRawValue().type);
        this.applySecretRule(!connection);
        this.formVisible = true;
    }

    save() {
        if (this.hasErrors()) {
            return;
        }
        this.saving = true;
        const datastoreCode = this.datastore.code;
        const payload = this.buildPayload();
        const call = this.editing
            ? this.fileConnectionService.updateFileConnectionUsingPUT(datastoreCode, this.editing.id, payload)
            : this.fileConnectionService.createFileConnectionUsingPOST(datastoreCode, payload);
        call.subscribe({
            next: () => {
                this.saving = false;
                this.formVisible = false;
                this.toast('success', 'fileConnections.messages.saved');
                this.load();
            },
            error: (err) => {
                this.saving = false;
                this.showError(err, 'fileConnections.messages.saveError');
            }
        });
    }

    test() {
        if (this.hasErrors()) {
            return;
        }
        this.testing = true;
        this.fileConnectionService.testFileConnectionUsingPOST(this.datastore.code, this.buildPayload()).subscribe({
            next: (res) => {
                this.testing = false;
                if (res?.message === 'Ok') {
                    this.toast('success', 'fileConnections.messages.testOk');
                } else {
                    this.messageService.add({ severity: 'error', summary: this.t('fileConnections.messages.error'), detail: this.testFailureText(res) });
                }
            },
            error: (err) => {
                this.testing = false;
                this.showError(err, 'fileConnections.messages.testError');
            }
        });
    }

    confirmDelete(connection: FileConnection) {
        this.confirmationService.confirm({
            message: this.t('fileConnections.messages.deleteConfirm', { name: connection.name }),
            acceptLabel: this.t('fileConnections.delete'),
            rejectLabel: this.t('fileConnections.dialog.cancel'),
            accept: () => this.delete(connection)
        });
    }

    private delete(connection: FileConnection) {
        this.fileConnectionService.deleteFileConnectionUsingDELETE(this.datastore.code, connection.id).subscribe({
            next: () => {
                this.toast('success', 'fileConnections.messages.deleted');
                this.load();
            },
            error: (err) => this.showError(err, 'fileConnections.messages.deleteError')
        });
    }

    private load() {
        this.loading = true;
        this.fileConnectionService.getFileConnectionsUsingGET(this.datastore.code).subscribe({
            next: (list) => {
                // Le serveur renvoie tout ce que l'appelant gère : on ne garde que le périmètre affiché.
                this.connections = (list ?? []).filter(c => (c.partner ?? null) === (this.partner ?? null));
                this.form.get('name').updateValueAndValidity();
                this.loading = false;
            },
            error: (err) => {
                this.connections = [];
                this.loading = false;
                this.showError(err, 'fileConnections.messages.loadError');
            }
        });
    }

    /** Seul le bloc du type choisi est actif : l'autre ne compte plus dans la validité du formulaire. */
    private applyType(type: string) {
        if (type === 'FTP') {
            this.form.get('s3').disable({ emitEvent: false });
            this.form.get('ftp').enable({ emitEvent: false });
        } else {
            this.form.get('ftp').disable({ emitEvent: false });
            this.form.get('s3').enable({ emitEvent: false });
        }
    }

    /**
     * Marque tous les champs pour afficher leurs erreurs (bordure rouge et message) ; vrai si le formulaire est
     * invalide. À la création le secret est obligatoire, à la modification vide = conservé (cf. applySecretRule).
     */
    private hasErrors(): boolean {
        this.markAllDirty(this.form);
        if (this.form.invalid) {
            this.toast('warn', 'fileConnections.messages.invalidForm');
            return true;
        }
        return false;
    }

    private markAllDirty(control: AbstractControl) {
        control.markAsDirty();
        control.markAsTouched();
        const children = (control as FormGroup).controls;
        if (children) {
            Object.values(children).forEach(child => this.markAllDirty(child));
        }
    }

    hasFieldError(path: string): boolean {
        const control = this.form.get(path);
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    private applySecretRule(required: boolean) {
        ['s3.secretKey', 'ftp.password'].forEach(path => {
            const control = this.form.get(path);
            if (required) {
                control.setValidators(Validators.required);
            } else {
                control.clearValidators();
            }
            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    private buildPayload(): FileConnection {
        const raw = this.form.getRawValue();
        const payload: FileConnection = {
            id: this.editing?.id,
            name: raw.name,
            description: raw.description,
            partner: this.partner ?? undefined,
            type: raw.type
        };
        if (raw.type === 'S3') {
            payload.s3 = { ...raw.s3 };
        } else {
            payload.ftp = { ...raw.ftp };
        }
        return payload;
    }

    /** Message du serveur s'il y en a un, sinon le texte (traduit) de repli. */
    private showError(err: any, fallbackKey: string) {
        const detail = err?.error?.detail || err?.error?.title || err?.error?.message;
        this.messageService.add({ severity: 'error', summary: this.t('fileConnections.messages.error'), detail: detail || this.t(fallbackKey) });
    }

    /** `messageOrKey` est une clé de traduction ; un texte venu du serveur est affiché tel quel. */
    private toast(severity: 'success' | 'warn' | 'error', messageOrKey: string) {
        const summaryKey = { success: 'success', warn: 'warning', error: 'error' }[severity];
        this.messageService.add({
            severity,
            summary: this.t(`fileConnections.messages.${summaryKey}`),
            detail: messageOrKey.startsWith('fileConnections.') ? this.t(messageOrKey) : messageOrKey
        });
    }

    /**
     * Le serveur renvoie un code stable (`code`) traduit ici ; sans code, le texte technique (`message`) est ajouté à un
     * libellé traduit. Aucun texte rédigé du serveur n'est affiché tel quel.
     */
    private testFailureText(res: { [key: string]: string } | null): string {
        const detail = res?.message;
        if (res?.code) {
            const key = `fileConnections.test.${res.code}`;
            const text = this.t(key, { detail });
            if (text !== key) {
                return text;
            }
        }
        return detail ? this.t('fileConnections.test.failed', { detail }) : this.t('fileConnections.messages.testFailed');
    }

    private t(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }
}
